import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { LuUsers, LuTrash2, LuCrown, LuSend, LuLogOut } from 'react-icons/lu'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'

import { getUserId } from '../lib/workos'
import useOrganizations, { orgLabel } from '../models/useOrganizations'
import useOrgMembers, { OrgMember } from '../models/useOrgMembers'
import useOrgInvites from '../models/useOrgInvites'
import { useOrgRole } from '../hooks/useOrgRole'
import useAuth from '../models/useAuth'
import useProfile from '../models/useProfile'
import { MEMBER_LIST_NOTICE, rosterView } from '../utilities/memberRoster'
import type { OrgRole } from '../utilities/orgRole'
import { useModalClose } from '../hooks/useModalClose'
import Modal from './Modal'
import DeleteConfirm from './DeleteConfirm'
import DialogConfirm from './DialogConfirm'
import RenameTitle from './RenameTitle'
import OrgSupportAccess from './OrgSupportAccess'
import UserLabel from './UserLabel'
import { userTextWithEmail } from '../utilities/userLabel'
import { bareInput } from '../utilities/formAlign'

type Props = {
  inline?: boolean
}

const ROLES = ['owner', 'admin', 'member']

// The member and invitation rows end in the same three columns: the role, one action (transfer
// ownership, or resend an invitation) and delete. Each column has a fixed width, and an empty one
// keeps its place, so the columns line up down both lists whatever each row shows.
const ROLE_WIDTH = 110
const ICON_WIDTH = 32 // a small IconButton: a 16px icon and 8px of padding on each side

// The name takes what the columns leave, and never less than 140px: on a phone, where the columns
// left it about 85px ("Admin P…"), the columns wrap to a line of their own, on the right.
const NAME = { flex: '1 1 140px' } as const

const RoleSlot = ({ children }: { children: ReactNode }) => (
  <Box sx={{ width: ROLE_WIDTH, flexShrink: 0, display: 'flex' }}>{children}</Box>
)
const IconSlot = ({ children }: { children?: ReactNode }) => (
  <Box sx={{ width: ICON_WIDTH, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>{children}</Box>
)

export default function Members({ inline }: Readonly<Props>) {
  const myUserId = getUserId()
  const { activeOrgId, role: myRole, organizations, rename } = useOrganizations()
  const { members, get: getMembers, subscribe: subscribeMembers, setRole, remove, transferOwnership, leave } = useOrgMembers()
  const { invites, get: getInvites, subscribe: subscribeInvites, invite, resend, revoke } = useOrgInvites()
  const { can } = useOrgRole()
  const myEmail = useAuth(state => state.user?.email ?? '')
  const myName = useProfile(state => state.fullName)

  const { open } = useModalClose('/', inline)
  const navigate = useNavigate()
  const location = useLocation()
  const handleClose = () => {
    if (inline) return
    if (location.key === 'default') navigate('/')
    else navigate(-1)
  }

  const [error, setError] = useState<string>()
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('member')
  const [sending, setSending] = useState(false)
  const [notice, setNotice] = useState<string>()

  const activeOrg = organizations.find(o => o.id === activeOrgId)
  // ⚠ `members.manage` is the SAME action the router gates `org_update` with
  // (`AuthScopes: ["members.manage"]`, `requireRole(… ActionManageMembers)`), so
  // Owner and Admin may rename and a Member may not. This only hides the control.
  const canManage = can('members.manage')
  // ⚠ Personal orgs are INCLUDED, and that is deliberate. router `0091` removed the
  // FailedPrecondition that used to refuse them (`16fa0ef2`): a personal org's name
  // is NULL until its owner picks one, the label falls back to the owner's profile
  // name and then their email, and naming it here PINS a label that a later profile
  // edit no longer overwrites. Do not add a `!personal` condition — it would take a
  // shipped capability away.
  const canRename = canManage && !!activeOrg
  // Only an Owner or Admin lists members and invites (PERMISSIONS.md § 5 Org, app#381). A Member
  // sees their own membership: the router refuses them `org_members_list` (router#1765), and after
  // migration 0227 RLS gives them no invite row, so the roster would read as empty.
  const view = rosterView(myRole as OrgRole)

  // Page-scoped load + realtime, re-run when the active org or the caller's role changes. A Member
  // neither reads nor subscribes: both would be refused, or empty, on the new router.
  useEffect(() => {
    if (!activeOrgId || view !== 'roster') return
    void getMembers(activeOrgId)
    void getInvites(activeOrgId)
    const unsubMembers = subscribeMembers(activeOrgId)
    const unsubInvites = subscribeInvites(activeOrgId)
    return () => {
      unsubMembers()
      unsubInvites()
    }
  }, [activeOrgId, view, getMembers, getInvites, subscribeMembers, subscribeInvites])

  const ownerCount = useMemo(() => members.filter(m => m.role === 'owner').length, [members])

  // An Admin may not touch an Owner; the sole Owner can't be demoted/removed. The
  // server is the real guard — these just disable the controls and explain why.
  const ownerLocked = (m: OrgMember) => myRole === 'admin' && m.role === 'owner'
  const isOnlyOwner = (m: OrgMember) => m.role === 'owner' && ownerCount <= 1
  const lockReason = (m: OrgMember) =>
    ownerLocked(m)
      ? 'Only an owner can manage owners'
      : isOnlyOwner(m)
        ? 'An organization must keep at least one owner'
        : ''

  const assignableRoles = myRole === 'owner' ? ROLES : ['admin', 'member']

  const onSetRole = async (m: OrgMember, role: string) => {
    if (!activeOrgId || role === m.role) return
    setError(undefined)
    try {
      await setRole(activeOrgId, m.userId, role)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to change role')
    }
  }

  const onRemove = async (m: OrgMember) => {
    if (!activeOrgId) return
    setError(undefined)
    try {
      await remove(activeOrgId, m.userId)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove member')
    }
  }

  const onTransfer = async (m: OrgMember) => {
    if (!activeOrgId) return
    setError(undefined)
    try {
      await transferOwnership(activeOrgId, m.userId)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to transfer ownership')
    }
  }

  const onLeave = async () => {
    if (!activeOrgId) return
    setError(undefined)
    try {
      await leave(activeOrgId)
      navigate('/') // re-scoped to the personal org; close the members modal
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to leave organization')
    }
  }

  const onInvite = async () => {
    const email = inviteEmail.trim()
    if (!activeOrgId || !email) return
    setError(undefined)
    setNotice(undefined)
    setSending(true)
    try {
      await invite(activeOrgId, email, inviteRole)
      setNotice(`Invitation email sent to ${email}.`)
      setInviteEmail('')
      setInviteRole('member')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send invitation')
    } finally {
      setSending(false)
    }
  }

  const onResend = async (i: { id: string; email: string }) => {
    setError(undefined)
    setNotice(undefined)
    try {
      await resend(i.id)
      setNotice(`Invitation email resent to ${i.email}.`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to resend invitation')
    }
  }

  const onRevoke = async (inviteId: string) => {
    setError(undefined)
    try {
      await revoke(inviteId)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to revoke invitation')
    }
  }

  return (
    <Modal
      open={open}
      inline={inline}
      maxWidth="sm"
      onClose={inline ? undefined : handleClose}
      color="secondary.main"
      icon={
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: '50%',
            bgcolor: 'secondary.main',
            color: 'background.default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 2,
            borderColor: 'background.default',
          }}
        >
          <LuUsers size={28} />
        </Box>
      }
    >
      <DialogContent>
        <Stack spacing={3}>
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              // Without a gap the title ran into the organization name at phone width.
              gap: 2
            }}>
            <Typography variant="subtitle1" color="secondary" sx={{
              flexGrow: 1
            }}>
              Members
            </Typography>
            {activeOrg && (
              // The same rename control the node and the network pages use (app#177).
              // ⚠ NO `dnsLabel`: an organization name is FREE TEXT. It is not part of
              // any DNS name, so the character filter, the lowercasing and
              // `REGEX_VALID_DOMAIN` must not reach it — "Acme Corp, Inc." is a valid
              // organization name and an invalid node name.
              // ⚠ The failure goes UNDER the field, not into the shared Alert below:
              // that Alert also carries the roster, the invite form and the leave
              // action, so a rename error there read as a page-wide failure.
              // The Box only stacks the error under the field; the width stays on the
              // field itself, as it was.
              <Box>
                <RenameTitle
                  value={activeOrg.name ?? ''}
                  subject="organization"
                  canRename={canRename}
                  onRename={name => rename(activeOrg.id, name)}
                  resetKey={activeOrg.id}
                  placeholder={orgLabel(activeOrg)}
                  sx={{ maxWidth: 240 }}
                  readOnly={<Typography variant="code">{orgLabel(activeOrg)}</Typography>}
                />
              </Box>
            )}
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}

          {/* A Member's own membership, in place of the roster (app#381). The role is the one
              the caller's own organization_members row gives, which every member may read. */}
          {view === 'self' && (
            <Stack spacing={1}>
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  py: 0.5
                }}>
                <Stack sx={{ minWidth: 0 }}>
                  {/* The name, with the email on hover; the email alone with no name (app#401). */}
                  <UserLabel user={{ name: myName, email: myEmail }} you fallback="You" noWrap />
                </Stack>
                {myRole && <Chip label={myRole} size="small" variant="outlined" />}
              </Stack>
              <Typography variant="body2" sx={{
                color: 'text.secondary'
              }}>
                {MEMBER_LIST_NOTICE}
              </Typography>
            </Stack>
          )}

          {/* Member roster — Owners and Admins only */}
          {view === 'roster' && (
            <Stack>
              {members.length === 0 ? (
                <Typography variant="body2" sx={{
                  color: 'text.disabled'
                }}>
                  No members
                </Typography>
              ) : (
                members.map(m => {
                  const editable = canManage && !ownerLocked(m) && !isOnlyOwner(m)
                  const reason = lockReason(m)
                  return (
                    <Stack
                      key={m.userId}
                      direction="row"
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        flexWrap: 'wrap',
                        py: { xs: 1, sm: 0.5 }
                      }}>
                      <Stack sx={{ ...NAME, minWidth: 0 }}>
                        {/* The name, with the email on hover; the email alone with no name (app#401). */}
                        <UserLabel user={m} you={m.userId === myUserId} noWrap />
                      </Stack>
                      <Stack
                        direction="row"
                        sx={{
                          alignItems: 'center',
                          ml: 'auto',
                          gap: 0.5
                        }}>
                        <RoleSlot>
                          {editable ? (
                            <Tooltip title={reason} placement="top" arrow>
                              <Select
                                size="small"
                                value={m.role}
                                onChange={e => onSetRole(m, e.target.value)}
                                sx={{ ...bareInput, width: ROLE_WIDTH }}
                              >
                                {/* Keep the current role visible even if it isn't otherwise assignable. */}
                                {[...new Set([m.role, ...assignableRoles])].map(r => (
                                  <MenuItem key={r} value={r}>
                                    {r}
                                  </MenuItem>
                                ))}
                              </Select>
                            </Tooltip>
                          ) : (
                            <Tooltip title={reason} placement="top" arrow>
                              <Chip label={m.role} size="small" variant="outlined" />
                            </Tooltip>
                          )}
                        </RoleSlot>

                        {canManage && (
                          <>
                            <IconSlot>
                              {myRole === 'owner' && m.role !== 'owner' && (
                                <DialogConfirm
                                  title="Transfer ownership"
                                  action="Transfer"
                                  color="secondary"
                                  dialogChildren={
                                    <Typography variant="body2">
                                      Make <strong>{userTextWithEmail(m)}</strong> the owner of this organization? You will
                                      become an admin.
                                    </Typography>
                                  }
                                  onConfirm={() => onTransfer(m)}
                                >
                                  <Tooltip title="Transfer ownership" placement="top" arrow>
                                    <IconButton size="small" color="secondary">
                                      <LuCrown size={16} />
                                    </IconButton>
                                  </Tooltip>
                                </DialogConfirm>
                              )}
                            </IconSlot>
                            <IconSlot>
                              {editable ? (
                                <DeleteConfirm
                                  message={`Remove ${userTextWithEmail(m)} from this organization?`}
                                  onConfirm={() => onRemove(m)}
                                >
                                  <Tooltip title="Remove" placement="top" arrow>
                                    <IconButton size="small">
                                      <LuTrash2 size={16} />
                                    </IconButton>
                                  </Tooltip>
                                </DeleteConfirm>
                              ) : (
                                <Tooltip title={reason} placement="top" arrow>
                                  <span>
                                    <IconButton size="small" disabled>
                                      <LuTrash2 size={16} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </IconSlot>
                          </>
                        )}
                      </Stack>
                    </Stack>
                  )
                })
              )}
            </Stack>
          )}

          {/* Pending invites + invite form — managers only */}
          {canManage && (
            <>
              <Divider />
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{
                  color: 'text.secondary'
                }}>
                  Pending invitations
                </Typography>
                {invites.length === 0 ? (
                  <Typography variant="body2" sx={{
                    color: 'text.disabled'
                  }}>
                    No pending invitations
                  </Typography>
                ) : (
                  invites.map(i => (
                    <Stack
                      key={i.id}
                      direction="row"
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        flexWrap: 'wrap',
                        py: { xs: 1, sm: 0.5 }
                      }}>
                      <Typography variant="body2" noWrap sx={NAME}>
                        {i.email}
                      </Typography>
                      <Stack
                        direction="row"
                        sx={{
                          alignItems: 'center',
                          ml: 'auto',
                          gap: 0.5
                        }}>
                        <RoleSlot>
                          <Chip label={i.role} size="small" variant="outlined" />
                        </RoleSlot>
                        <IconSlot>
                          <Tooltip title="Resend invitation" placement="top" arrow>
                            <IconButton size="small" color="secondary" onClick={() => onResend(i)}>
                              <LuSend size={16} />
                            </IconButton>
                          </Tooltip>
                        </IconSlot>
                        <IconSlot>
                          <Tooltip title="Revoke" placement="top" arrow>
                            <IconButton size="small" color="error" onClick={() => onRevoke(i.id)}>
                              <LuTrash2 size={16} />
                            </IconButton>
                          </Tooltip>
                        </IconSlot>
                      </Stack>
                    </Stack>
                  ))
                )}

                {notice && <Alert severity="success">{notice}</Alert>}

                <Stack
                  direction="row"
                  sx={{
                    gap: 1,
                    // The field's label sits ABOVE its box and is part of it (`utilities/formAlign.ts`),
                    // so a centred row put the button 12px high. The field has no helper text, so
                    // its bottom is the bottom of the box.
                    alignItems: 'flex-end',
                    // At phone width the email takes a line of its own; beside the role and the
                    // button it was squeezed to about 100px.
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    pt: 1
                  }}>
                  <TextField
                    size="small"
                    type="email"
                    label="Email"
                    placeholder="person@company.com"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 0 } }}
                  />
                  <Select size="small" value={inviteRole} onChange={e => setInviteRole(e.target.value)} sx={{ ...bareInput, width: ROLE_WIDTH }}>
                    <MenuItem value="member">member</MenuItem>
                    <MenuItem value="admin">admin</MenuItem>
                    {myRole === 'owner' && <MenuItem value="owner">owner</MenuItem>}
                  </Select>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={onInvite}
                    disabled={sending || !inviteEmail.trim()}
                  >
                    Invite
                  </Button>
                </Stack>
              </Stack>
            </>
          )}

          {/* "Allow noBGP support" and the temporary grant (app#376, PERMISSIONS.md § 8). Every member
              reads it; an org Owner or Admin changes it. Keyed on the org so a switch starts clean. */}
          {activeOrgId && (
            <>
              <Divider />
              <OrgSupportAccess key={activeOrgId} orgId={activeOrgId} />
            </>
          )}

          {/* Leave organization — shown for any org except your OWN personal org
              (a personal org's id == your profile id; you can't leave that one, but
              you can leave one you were invited into). The sole Owner can't leave
              (must transfer first); the server is the real guard. */}
          {activeOrg && !(activeOrg.personal && activeOrg.id === myUserId) && (
            <>
              <Divider />
              {myRole === 'owner' && ownerCount <= 1 ? (
                <Tooltip title="Transfer ownership to another member before you can leave" placement="top" arrow>
                  <span>
                    <Button color="error" variant="outlined" startIcon={<LuLogOut size={16} />} disabled>
                      Leave organization
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <DialogConfirm
                  title="Leave organization"
                  action="Leave"
                  color="error"
                  dialogChildren={
                    <Typography variant="body2">
                      Leave <strong>{orgLabel(activeOrg)}</strong>? You&apos;ll lose access to its networks, nodes, and
                      services.
                    </Typography>
                  }
                  onConfirm={onLeave}
                >
                  <Button color="error" variant="outlined" startIcon={<LuLogOut size={16} />}>
                    Leave organization
                  </Button>
                </DialogConfirm>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
    </Modal>
  )
}
